import React, { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Tooltip,
  TextField,
  Popover,
  TablePagination,
  Alert,
  Chip,
  styled,
} from '@mui/material';
import {
  ManageSearch as ManageSearchIcon,
  Visibility as VisibilityIcon,
  GroupAdd as GroupAddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Replay as ReplayIcon
} from '@mui/icons-material';
import Lottie from 'lottie-react';
import loadingAnimation from './loading-animation.json';
import ReasignarProcuradorModal from './ReasignarProcuradorModal';
import ReaperturaDemanda from './ReaperturaDemanda';
import { useNavigate } from 'react-router-dom';
import { ContextSession } from "../../services/ContextSession";

// Componente memorizado para celdas de procurador
const ProcuradorCell = React.memo(({ procurador }) => (
  <TableCell align="center">
    {procurador?.nombre || 'No asignado'}
  </TableCell>
));

// Estilos
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  '&.MuiTableCell-head': {
    backgroundColor: '#19438C',
    color: 'white',
    fontSize: 16,
  },
  '&.MuiTableCell-body': {
    fontSize: 14,
    borderRight: 'none',
    borderLeft: 'none',
  },
}));

const ListadoDeDemandas = () => {
  const navigate = useNavigate();
  const [demandas, setDemandas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [filterType, setFilterType] = useState('');
  const [searchName, setSearchName] = useState('');
  const [searchID, setSearchID] = useState('');
  const [searchExpediente, setSearchExpediente] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [modalReasignarOpen, setModalReasignarOpen] = useState(false);
  const [demandaAReasignar, setDemandaAReasignar] = useState(null);
  const [procuradoresLista, setProcuradoresLista] = useState([]);
  
  // Estados para la reapertura
  const [modalReaperturaOpen, setModalReaperturaOpen] = useState(false);
  const [demandaAReabrir, setDemandaAReabrir] = useState(null);
  
  let sessi = useContext(ContextSession);

  // Función para verificar si el usuario tiene rol LegalJefe
  const esLegalJefe = useCallback(() => {
    return sessi?.sessionData?.access?.some(modulo => modulo.rol === "LegalJefe") || false;
  }, [sessi]);

  // Obtener ID del usuario actual
  const usuarioActualId = useMemo(() => {
    return sessi?.sessionData?.id || null;
  }, [sessi]);

  // Agregar esta función para determinar roles
const determinarRolUsuario = useCallback(() => {
  if (!sessi?.sessionData?.access) return 'desconocido';
  
  const modulos = sessi.sessionData.access;
  
  if (modulos.some(modulo => modulo.rol === "LegalJefe")) {
    return "LegalJefe";
  } else if (modulos.some(modulo => modulo.rol === "LegalProcuradorTGU")) {
    return "LegalProcuradorTGU";
  } else {
    return "desconocido";
  }
}, [sessi]);

// Memoizar los permisos
const rolUsuario = useMemo(() => determinarRolUsuario(), [determinarRolUsuario]);
const esUsuarioLegalJefe = useMemo(() => rolUsuario === "LegalJefe", [rolUsuario]);
const esLegalProcuradorTGU = useMemo(() => rolUsuario === "LegalProcuradorTGU", [rolUsuario]);

  // Crear mapa de procuradores para acceso rápido
  const procuradoresMap = useMemo(() => {
    return procuradoresLista.reduce((map, proc) => {
      map[proc.id] = proc;
      return map;
    }, {});
  }, [procuradoresLista]);

  // Obtener datos optimizado
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const token = sessi.sessionData.token;

        const [responseDemandas, responseProcuradores] = await Promise.all([
          fetch('https://bei360.injupemp.gob.hn:8444/api/tramite/listar/?sucursalID=2', {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            }
          }),
          fetch('https://bei360.injupemp.gob.hn:8444/api/auth/oficialesLegal/', {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            }
          })
        ]);

        if (!responseDemandas.ok) throw new Error(`Error HTTP! estado: ${responseDemandas.status}`);
        if (!responseProcuradores.ok) throw new Error(`Error HTTP! estado: ${responseProcuradores.status}`);

        const dataDemandas = await responseDemandas.json();
        const dataProcuradores = await responseProcuradores.json();
        
        console.log('Datos de demandas recibidos:', dataDemandas);
        console.log('Datos de procuradores recibidos:', dataProcuradores);

        // En el useEffect, reemplaza esta parte:
        const demandasProcesadas = dataDemandas.map(demanda => {
          // Determinar el nombre del demandante basado en la naturaleza
          let nombreDemandante = '';
          if (demanda.naturaleza === 1) { // A Favor
            nombreDemandante = demanda.nombredemandante || 'N/A';
          } else if (demanda.naturaleza === 2) { // En Contra
            nombreDemandante = demanda.nombredemandado || 'N/A';
          }

          let procuradorInfo = null;
          if (demanda.procuradorasignado) {
            if (isNaN(demanda.procuradorasignado)) {
              procuradorInfo = { nombre: demanda.procuradorasignado };
            } else {
              const procuradorId = parseInt(demanda.procuradorasignado);
              procuradorInfo = dataProcuradores.find(p => p.id === procuradorId) || null;
            }
          }
          
          // CORREGIR: Lógica para determinar si está asignada al usuario actual
          const asignadaAlUsuarioActual = (() => {
            if (!demanda.procuradorasignado) return false;
            
            // Si procuradorasignado es un ID numérico (ej: "71")
            if (!isNaN(demanda.procuradorasignado)) {
              return parseInt(demanda.procuradorasignado) === usuarioActualId;
            }
            
            // Si procuradorasignado es un nombre string (ej: "KELIN RAQUEL FERRERA")
            // Buscar en los procuradores si alguno coincide con el usuario actual
            const procuradorUsuario = dataProcuradores.find(p => p.id === usuarioActualId);
            if (procuradorUsuario && procuradorUsuario.nombre === demanda.procuradorasignado) {
              return true;
            }
            
            return false;
          })();
          
          return {
            ...demanda,
            tramiteid: demanda.id || demanda.tramiteid,
            demandante: nombreDemandante,
            expediente: demanda.numeroexpediente || 'N/A',
            asunto: demanda.motivodemanda || 'N/A',
            fechaaudiencia: demanda.fechamaximacontestacion || null,
            estado: demanda.estado,
            naturaleza: demanda.naturaleza,
            nombredemandante: demanda.nombredemandante,
            nombredemandado: demanda.nombredemandado,
            procuradorInfo: procuradorInfo,
            procuradorasignado: demanda.procuradorasignado || 'No asignado',
            moneda: demanda.moneda,
            montoestimacion: demanda.montoestimacion,
            observaciones: demanda.observaciones,
            asignadaAlUsuarioActual: asignadaAlUsuarioActual
          };
        });

        setDemandas(demandasProcesadas);
        setProcuradoresLista(dataProcuradores);
      } catch (err) {
        setError(err.message);
        console.error('Error al obtener los datos:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [sessi.sessionData.token, usuarioActualId]);

  
  // Función para determinar el estado de la demanda
  const getEstadoDemanda = (demanda) => {
    if (demanda.estado) return demanda.estado;
    
    const hoy = new Date();
    const fechaAudiencia = demanda.fechamaximacontestacion ? new Date(demanda.fechamaximacontestacion) : null;
    
    if (fechaAudiencia && fechaAudiencia < hoy) {
      return 'Vencido';
    }
    
    return 'Activo';
  };

  // Handlers optimizados con useCallback
  const handleTabChange = useCallback((event, newValue) => {
    setTabIndex(newValue);
    setPage(0);
  }, []);

  const handleFilterClick = useCallback((event, type) => {
    setAnchorEl(event.currentTarget);
    setFilterType(type);
  }, []);

  const handleFilterClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleSearch = useCallback((setter) => (event) => {
    setter(event.target.value);
    setPage(0);
  }, []);

  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  // Handler para navegar a la página de edición
  const handleOpenEditarPage = useCallback((demanda) => {
    sessi.setVariable("demandaID", demanda.tramiteid);
    sessi.setVariable("demandaData", demanda);
    navigate(`/Legal/EditarDemandaSps`);
  }, [navigate, sessi]);

  // Handlers para reasignar procurador
  const handleOpenReasignarModal = useCallback((demanda) => {
    setDemandaAReasignar(demanda);
    setModalReasignarOpen(true);
  }, []);

  const handleCloseReasignarModal = useCallback((success = false) => {
    setModalReasignarOpen(false);
    if (success) {
      setDemandaAReasignar(null);
    }
  }, []);

  // Handlers para reapertura de demanda
  const handleOpenReaperturaModal = useCallback((demanda) => {
    setDemandaAReabrir(demanda);
    setModalReaperturaOpen(true);
  }, []);

  const handleCloseReaperturaModal = useCallback(() => {
    setModalReaperturaOpen(false);
    setDemandaAReabrir(null);
  }, []);

  // Función para manejar la reapertura exitosa
  const handleReaperturaSuccess = useCallback((demandaActualizada) => {
    setDemandas(prevDemandas => {
      const index = prevDemandas.findIndex(d => d.tramiteid === demandaActualizada.tramiteid);
      if (index === -1) return prevDemandas;

      const updatedDemandas = [...prevDemandas];
      updatedDemandas[index] = {
        ...updatedDemandas[index],
        ...demandaActualizada,
        estado: 'En proceso'
      };

      return updatedDemandas;
    });

    handleCloseReaperturaModal();
  }, [handleCloseReaperturaModal]);

  const handleConfirmReasignacion = useCallback(async (nuevoProcuradorId) => {
    try {
      const token = sessi.sessionData.token;

      const response = await fetch('https://bei360.injupemp.gob.hn:8444/api/tramites/reasignar-procurador/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          tramite_id: demandaAReasignar.tramiteid,
          nuevo_procurador_id: nuevoProcuradorId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al reasignar el procurador');
      }

      const data = await response.json();
      const nuevoProcurador = procuradoresMap[nuevoProcuradorId];

      setDemandas(prevDemandas => {
        const index = prevDemandas.findIndex(d => d.tramiteid === demandaAReasignar.tramiteid);
        if (index === -1) return prevDemandas;

        const updatedDemandas = [...prevDemandas];
        updatedDemandas[index] = {
          ...updatedDemandas[index],
          usuarioprocuradorid: nuevoProcuradorId,
          procuradorasignado: nuevoProcurador?.nombre || 'No asignado',
          procuradorInfo: nuevoProcurador,
          asignadaAlUsuarioActual: nuevoProcuradorId === usuarioActualId
        };

        return updatedDemandas;
      });

      return { success: true, message: data.message };
    } catch (err) {
      console.error('Error al reasignar procurador:', err);
      return { success: false, message: err.message || 'Ocurrió un error al reasignar el procurador' };
    }
  }, [demandaAReasignar, procuradoresMap, sessi.sessionData.token, usuarioActualId]);

  const handleOpenAccionesModal = useCallback((demanda) => {
    sessi.setVariable("demandaID", demanda.tramiteid);
    navigate(`/Legal/AccionesDemanda`);
  }, [navigate, sessi]);

 // Filtrado y paginación optimizados CON FILTRO POR ROL
const filteredDemandas = useMemo(() => {
  let demandasFiltradas = demandas;

  // Si es Procurador (no LegalJefe), filtrar solo las demandas asignadas al usuario actual
  if (esLegalProcuradorTGU && !esUsuarioLegalJefe) {
    demandasFiltradas = demandas.filter(demanda => 
      demanda.asignadaAlUsuarioActual === true
    );
    console.log(`Usuario es Procurador. Mostrando ${demandasFiltradas.length} demandas asignadas de ${demandas.length} totales`);
  } 
  // Si es LegalJefe, mostrar todas las demandas
  else if (esUsuarioLegalJefe) {
    console.log(`Usuario ES LegalJefe. Mostrando todas las ${demandas.length} demandas`);
  }
  // Si no tiene rol definido, no mostrar nada
  else {
    demandasFiltradas = [];
    console.log(`Usuario sin rol definido. No se muestran demandas`);
  }

  // Aplicar filtros de pestaña y búsqueda
  return demandasFiltradas.filter(demanda => {
    const tabFilter = tabIndex === 0 ?
      demanda.estado !== 'Cerrado' && demanda.estado !== 'Finalizado' :
      demanda.estado === 'Cerrado' || demanda.estado === 'Finalizado';

    const searchFilter =
      (searchID ? demanda.tramiteid.toString().includes(searchID) : true) &&
      (searchName ? demanda.demandante?.toLowerCase().includes(searchName.toLowerCase()) : true) &&
      (searchExpediente ? demanda.expediente?.toLowerCase().includes(searchExpediente.toLowerCase()) : true);

    return tabFilter && searchFilter;
  });
}, [demandas, esUsuarioLegalJefe, esLegalProcuradorTGU, tabIndex, searchID, searchName, searchExpediente]);

  const paginatedDemandas = useMemo(() => {
    return filteredDemandas.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [filteredDemandas, page, rowsPerPage]);

  const formatFecha = useCallback((fechaString) => {
    if (!fechaString) return 'N/A';
    try {
      const fecha = new Date(fechaString);
      if (isNaN(fecha.getTime())) return 'N/A';
      
      fecha.setMinutes(fecha.getMinutes() + fecha.getTimezoneOffset());
      return fecha.toLocaleDateString('es-HN');
    } catch (error) {
      console.error('Error formateando fecha:', fechaString, error);
      return 'N/A';
    }
  }, []);

  // Estado de carga con animación Lottie
  if (loading && demandas.length === 0) {
    return (
      <Container style={{
        marginTop: 15,
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '70vh'
      }}>
        <Lottie
          animationData={loadingAnimation}
          loop={true}
          autoplay={true}
          style={{ width: 200, height: 200 }}
        />
        <Typography variant="h6" mt={2}>Cargando listado de demandas...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container style={{ marginTop: 15, textAlign: 'center' }}>
        <Alert severity="error">Error al cargar las demandas: {error}</Alert>
      </Container>
    );
  }

  return (
    <Container  style={{ marginTop: 15 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="h4" fontWeight="bold">Lista Demandas</Typography>
        {esLegalProcuradorTGU && (
          <Chip 
            label="Mis Demandas" 
            color="primary" 
            size="small" 
            sx={{ ml: 2 }} 
          />
        )}
      </div>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          margin: '0,5px 0',
          height: '120px'
        }}
      >
        <Lottie
          animationData={loadingAnimation}
          loop={true}
          autoplay={true}
          style={{ width: 200, height: 170 }}
        />
      </Box>
      <Box sx={{ position: 'sticky', top: 0, zIndex: 1, display: 'flex', justifyContent: 'center', my: 5 }}>
        <Paper square>
          <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            sx={{
              '& .MuiTabs-indicator': {
                backgroundColor: '#19438C',
              },
            }}
          >
            <Tab
              label="Casos Activos"
              sx={{
                color: tabIndex === 0 ? '#19438C' : '#000001',
                fontWeight: 'medium',
                '&.Mui-selected': {
                  color: '#19438C',
                },
              }}
            />
            <Tab
              label="Casos Cerrados"
              sx={{
                color: tabIndex === 1 ? '#4CAF50' : '#000001',
                fontWeight: 'medium',
                '&.Mui-selected': {
                  color: '#19438C',
                },
              }}
            />
          </Tabs>
        </Paper>
      </Box>

      <TableContainer sx={{widt:"1200px"}} component={Paper}>
        <Table>
          <TableHead>
            <StyledTableRow>
              <StyledTableCell align="center">
                <Box display="flex" alignItems="center" justifyContent="center">
                  <strong>ID Demanda</strong>
                  <Tooltip title="Buscar por ID Demanda" arrow>
                    <IconButton onClick={(e) => handleFilterClick(e, 'searchID')}>
                      <ManageSearchIcon style={{ color: 'white' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </StyledTableCell>

              <StyledTableCell align="center">
                <Box display="flex" alignItems="center" justifyContent="center">
                  <strong>Demandante</strong>
                  <Tooltip title="Buscar Demandante" arrow>
                    <IconButton onClick={(e) => handleFilterClick(e, 'search')}>
                      <ManageSearchIcon style={{ color: 'white' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </StyledTableCell>

               <StyledTableCell align="center">
                <Box display="flex" alignItems="center" justifyContent="center">
                  <strong>Demandando</strong>
                  <Tooltip title="Buscar Demandando" arrow>
                    <IconButton onClick={(e) => handleFilterClick(e, 'search')}>
                      <ManageSearchIcon style={{ color: 'white' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </StyledTableCell>

              <StyledTableCell align="center">
                <Box display="flex" alignItems="center" justifyContent="center">
                  <strong>N° Expediente</strong>
                  <Tooltip title="Buscar por N° de Expediente" arrow>
                    <IconButton onClick={(e) => handleFilterClick(e, 'searchExpediente')}>
                      <ManageSearchIcon style={{ color: 'white' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </StyledTableCell>
              <StyledTableCell align="center"><strong>Fecha Máx. Contestación</strong></StyledTableCell>
              <StyledTableCell align="center"><strong>Estado</strong></StyledTableCell>
              <StyledTableCell align="center"><strong>Procurador</strong></StyledTableCell>
              <StyledTableCell align="center"><strong>Acciones</strong></StyledTableCell>
            </StyledTableRow>
          </TableHead>

          <TableBody>
            {paginatedDemandas.length > 0 ? (
              paginatedDemandas.map((demanda) => (
                <StyledTableRow key={demanda.tramiteid}>
                  <TableCell align="center">{demanda.tramiteid}</TableCell>
                  <TableCell align="center">
                    {demanda.nombredemandante && demanda.nombredemandante.trim() !== '' 
                      ? demanda.nombredemandante 
                      : 'NO APLICA'}
                  </TableCell>
                  <TableCell align="center">
                    {demanda.nombredemandado && demanda.nombredemandado.trim() !== '' 
                      ? demanda.nombredemandado 
                      : 'NO APLICA'}
                  </TableCell>
                  <TableCell align="center">{demanda.expediente}</TableCell>
                  <TableCell align="center">{formatFecha(demanda.fechamaximacontestacion)}</TableCell>
                  <TableCell align="center">
                    <Chip
                      variant="outlined"
                      label={demanda.estado || 'N/A'}
                      color={
                        demanda.estado === 'En proceso' ? 'primary' :
                        demanda.estado === 'Cerrado' ? 'success' :
                        demanda.estado === 'Creada' ? 'warning' : 'default'
                      }
                    />
                  </TableCell>
                  <ProcuradorCell procurador={demanda.procuradorInfo} />
                  <TableCell align="center">
                    <Box display="flex" justifyContent="center" gap={1}>
                      <Tooltip title="Ver detalles de demanda" arrow>
                        <IconButton
                          onClick={() => handleOpenAccionesModal(demanda)}
                          sx={{
                            color: '#20c997',
                            backgroundColor: 'rgba(25, 118, 210, 0.1)',
                            '&:hover': {
                              backgroundColor: 'rgba(25, 118, 210, 0.2)',
                            }
                          }}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>

                      {/* Botón de editar - Solo visible si NO está cerrado */}
                      {demanda.estado?.toLowerCase() !== 'cerrado' && (
                        <Tooltip title="Editar demanda" arrow>
                          <IconButton
                            onClick={() => handleOpenEditarPage(demanda)}
                            sx={{
                              color: '#ff9800',
                              backgroundColor: 'rgba(255, 152, 0, 0.1)',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 152, 0, 0.2)',
                              }
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* Botón de reasignar procurador - Solo visible si NO está cerrado, tiene procurador y es LegalJefe */}
                      {demanda.estado?.toLowerCase() !== 'cerrado' && demanda.procuradorInfo && esUsuarioLegalJefe && (
                        <Tooltip title="Reasignar procurador" arrow>
                          <IconButton
                            onClick={() => handleOpenReasignarModal(demanda)}
                            sx={{
                              color: '#19438C',
                              backgroundColor: 'rgba(107, 197, 208, 0.1)',
                              '&:hover': {
                                backgroundColor: 'rgba(107, 197, 208, 0.2)',
                              }
                            }}
                          >
                            <GroupAddIcon />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* Botón de reapertura - Solo visible si está cerrado y es LegalJefe */}
                      {demanda.estado?.toLowerCase() === 'cerrado' && esUsuarioLegalJefe && (
                        <Tooltip title="Reaperturar demanda" arrow>
                          <IconButton
                            onClick={() => handleOpenReaperturaModal(demanda)}
                            sx={{
                              color: '#19438C',
                              backgroundColor: 'rgba(107, 197, 208, 0.1)',
                              '&:hover': {
                                backgroundColor: 'rgba(107, 197, 208, 0.2)',
                              }
                            }}
                          >
                            <ReplayIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </StyledTableRow>
              ))
            ) : (
              <StyledTableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 0.2 }}>
                  <Box display="flex" flexDirection="column" alignItems="center">
                    <Typography variant="h6" color="text.secondary">
                      {esUsuarioLegalJefe 
                        ? (tabIndex === 0 ? 'No se encontraron demandas activas' : 'No se encontraron demandas cerradas')
                        : (tabIndex === 0 ? 'No tienes demandas activas asignadas' : 'No tienes demandas cerradas asignadas')
                      }
                    </Typography>
                    {(searchName || searchID || searchExpediente) && (
                      <Typography variant="body2" color="text.secondary" mt={1}>
                        Intenta con otros términos de búsqueda
                      </Typography>
                    )}
                    {!esUsuarioLegalJefe && filteredDemandas.length === 0 && (
                      <Typography variant="body2" color="text.secondary" mt={1}>
                        Solo puedes ver las demandas asignadas a tu usuario
                      </Typography>
                    )}
                  </Box>
                </TableCell>
              </StyledTableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleFilterClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Box p={2}>
          {filterType === 'search' && (
            <TextField
              placeholder="Buscar por Nombre"
              variant="outlined"
              value={searchName}
              onChange={handleSearch(setSearchName)}
              InputProps={{ startAdornment: <SearchIcon /> }}
            />
          )}
          {filterType === 'searchID' && (
            <TextField
              placeholder="Buscar por ID"
              variant="outlined"
              value={searchID}
              onChange={handleSearch(setSearchID)}
              InputProps={{ startAdornment: <SearchIcon /> }}
            />
          )}
          {filterType === 'searchExpediente' && (
            <TextField
              placeholder="Buscar por N° Expediente"
              variant="outlined"
              value={searchExpediente}
              onChange={handleSearch(setSearchExpediente)}
              InputProps={{ startAdornment: <SearchIcon /> }}
            />
          )}
        </Box>
      </Popover>

      {filteredDemandas.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 20]}
          component="div"
          count={filteredDemandas.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      )}

      <ReasignarProcuradorModal
        open={modalReasignarOpen}
        onClose={handleCloseReasignarModal}
        onConfirm={handleConfirmReasignacion}
        demandaId={demandaAReasignar?.tramiteid}
        procuradorActual={demandaAReasignar?.procuradorInfo}
        procuradoresLista={procuradoresLista}
      />

      {/* Modal de reapertura usando el componente separado */}
      <ReaperturaDemanda
        open={modalReaperturaOpen}
        onClose={handleCloseReaperturaModal}
        onSuccess={handleReaperturaSuccess}
        demanda={demandaAReabrir}
        token={sessi.sessionData.token}
      />
    </Container>
  );
};

export default ListadoDeDemandas;